import { apiClient, ApiResponse } from '@packages/api-client';

// 挂载类型枚举
export type MountType = 'agent-chat' | 'workflow' | 'knowledge-base';

// 校验任务状态枚举（精简三态）
export type OntologyValidationTaskStatus =
  | 'RUNNING'
  | 'COMPLETED'
  | 'FAILED';

// 校验结果级别
export type PublishValidationLevel = 'ERROR' | 'WARNING' | 'INFO';

// 阶段2（数据映射）details 结构
export interface MappingValidationDetails {
  sql?: string;
  data?: Record<string, unknown>;
  error?: string;
}

// 阶段3（Process 执行）details 结构
export interface ProcessValidationDetails {
  inputData?: Record<string, unknown>;
  outputData?: Record<string, unknown>;
  userLogs?: string;
  stdout?: string;
  stderr?: string;
  executionTimeMs?: number;
  errorMessage?: string;
}

// 单项校验结果
export interface PublishValidationItem {
  checkName: string;
  phaseName: string;
  phaseOrder: number;
  level: PublishValidationLevel;
  passed: boolean;
  message: string;
  elementName: string | null;
  /** 结构化详情，phaseOrder=2 为 MappingValidationDetails，phaseOrder=3 为 ProcessValidationDetails，其他阶段为通用 map */
  details?:
    | MappingValidationDetails
    | ProcessValidationDetails
    | Record<string, unknown>
    | null;
}

// 校验任务详情
export interface OntologyValidationTask {
  id: number;
  ontologyId: number;
  status: OntologyValidationTaskStatus;
  currentPhase: string;
  totalSteps: number;
  completedSteps: number;
  progressPercent: number;
  /** 是否存在 ERROR 级别校验项 */
  hasError: boolean;
  /** 是否存在 WARNING 级别校验项 */
  hasWarning: boolean;
  /** 是否可发布（status=COMPLETED 且无 ERROR） */
  publishable: boolean;
  validationResults: PublishValidationItem[];
  errorMessage: string | null;
  startedAt: string;
  finishedAt: string | null;
  createdTime: string;
  /** 任务创建时草稿版本的 updatedTime 快照，用于判断校验后草稿是否被修改 */
  draftVersionUpdatedTime: string | null;
  /** 当前配置下启用的校验阶段列表（按 order 升序），用于前端动态渲染 Steps */
  enabledPhases: ValidationPhaseDescriptor[];
}

/** 校验阶段描述（由后端按配置动态返回） */
export interface ValidationPhaseDescriptor {
  /** 阶段唯一标识（kebab-case） */
  name: string;
  /** 阶段顺序 */
  order: number;
  /** 阶段展示名 */
  phaseName: string;
}

// 挂载数据接口
export interface OntologyMount {
  id: number;
  name: string;
  mountType: MountType;
  appId: string;
  appCreator: string;
  ontologyId: number;
  ontologyName: string;
  createdTime: string;
}

// 本体详情数据接口
export interface OntologyDetail {
  id: number;
  name: string;
  description: string;
  definition: string; // 添加定义字段
  tags: string[];
  status: 'PUBLISHED' | 'UNPUBLISHED';
  visibility: 'PUBLIC' | 'PRIVATE';
  objectCount: number;
  relationCount: number;
  processCount: number;
  statusCount: number;
  createdTime: string;
  updatedTime: string;
  publishedBy: number;
  publishedTime: string;
  currentVersionId: number;
  versions: any[];
  canView: boolean;
  canEdit: boolean;
  canManage: boolean;
}

// 本体API类
export class OntologyApi {
  /**
   * 获取本体详情信息
   * @param id 本体ID
   * @returns 本体详情数据
   */
  static async getOntologyDetail(id: number): Promise<OntologyDetail> {
    try {
      console.log(`🚀 获取本体详情: ${id}`);

      const response = await apiClient.get<OntologyDetail>(`/ontologies/${id}`);

      console.log('📨 本体详情响应:', response);

      return response.data;
    } catch (error: any) {
      console.error('❌ 获取本体详情失败:', error);
      throw error;
    }
  }

  /**
   * 更新本体基础信息
   * @param id 本体ID
   * @param data 更新数据
   * @returns 更新后的本体信息
   */
  static async updateOntologyInfo(
    id: number,
    data: Partial<OntologyDetail>
  ): Promise<OntologyDetail> {
    try {
      console.log(`🚀 更新本体信息: ${id}`, data);

      const response = await apiClient.put<OntologyDetail>(
        `/ontologies/${id}`,
        data
      );

      console.log('📨 更新本体响应:', response);

      return response.data;
    } catch (error: any) {
      console.error('❌ 更新本体信息失败:', error);
      throw error;
    }
  }

  /**
   * 同步发布本体。发布前须先完成一次校验任务；后端按 ontologyId 自动定位最近一次 COMPLETED 校验结果。
   * @param id 本体ID
   * @param data 发布参数
   * @returns 新发布版本 ID
   */
  static async publishOntology(
    id: number,
    data: {
      versionName: string;
      versionDescription?: string;
      ignoreWarnings?: boolean;
    }
  ): Promise<number> {
    try {
      console.log(`🚀 发布本体: ${id}`, data);

      const response = await apiClient.post<number>(
        `/ontologies/${id}/publish`,
        data
      );

      console.log('✅ 发布本体成功, versionId:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('❌ 发布本体失败:', error);
      throw error;
    }
  }

  /**
   * 创建校验任务（异步执行），立即返回任务 ID。
   * @param id 本体ID
   * @returns 校验任务ID
   */
  static async createValidation(id: number): Promise<number> {
    try {
      console.log(`🚀 创建校验任务: ${id}`);

      const response = await apiClient.post<number>(
        `/ontologies/${id}/validations`
      );

      console.log('✅ 校验任务已创建, taskId:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('❌ 创建校验任务失败:', error);
      throw error;
    }
  }

  /**
   * 查询指定校验任务详情（轮询用）
   * @param ontologyId 本体ID
   * @param taskId 校验任务ID
   */
  static async getValidation(
    ontologyId: number,
    taskId: number
  ): Promise<OntologyValidationTask> {
    try {
      const response = await apiClient.get<OntologyValidationTask>(
        `/ontologies/${ontologyId}/validations/${taskId}`
      );
      return response.data;
    } catch (error: any) {
      console.error('❌ 查询校验任务失败:', error);
      throw error;
    }
  }

  /**
   * 查询本体最近一次校验任务（用于打开详情页时恢复视图）
   * @param ontologyId 本体ID
   * @returns 最近一次任务；无任务时返回 null
   */
  static async getLatestValidation(
    ontologyId: number
  ): Promise<OntologyValidationTask | null> {
    try {
      const response = await apiClient.get<OntologyValidationTask | null>(
        `/ontologies/${ontologyId}/validations/latest`
      );
      return response.data ?? null;
    } catch (error: any) {
      if (error?.response?.status === 404 || error?.status === 404) {
        return null;
      }
      console.error('❌ 查询最近校验任务失败:', error);
      throw error;
    }
  }

  /**
   * 下线本体
   * @param id 本体ID
   * @returns 下线结果
   */
  static async unpublishOntology(id: number): Promise<void> {
    try {
      console.log(`🚀 下线本体: ${id}`);

      await apiClient.post(`/ontologies/${id}/unpublish`);

      console.log('✅ 下线本体成功');
    } catch (error: any) {
      console.error('❌ 下线本体失败:', error);
      throw error;
    }
  }

  /**
   * 获取本体挂载列表
   * @param ontologyId 本体ID
   * @returns 挂载数据列表
   */
  static async getOntologyMounts(ontologyId: number): Promise<OntologyMount[]> {
    try {
      console.log(`🚀 获取本体挂载列表: ${ontologyId}`);

      const response = await apiClient.get<OntologyMount[]>(
        `/ontology-mounts?ontologyId=${ontologyId}`
      );

      console.log('📨 本体挂载列表响应:', response);

      return response.data;
    } catch (error: any) {
      console.error('❌ 获取本体挂载列表失败:', error);
      throw error;
    }
  }
}
